/* global Office, Excel */

Office.onReady((info) => {
  initializeApp(info);
});

const STORAGE_KEY = "eakesExcelTools.padCells.v6";

const appState = {
  activeToolId: "pad-cells",
  isBusy: false,
  officeHost: null
};

const PAD_PRESETS = [
  {
    id: "ddms-cust-acct",
    label: "DDMS — Cust Acct #",
    description: "10 characters, left padded with spaces",
    length: 10,
    padChar: " ",
    side: "left"
  },
  {
    id: "ddms-slsm",
    label: "DDMS — SLSM #",
    description: "4 characters, left padded with spaces",
    length: 4,
    padChar: " ",
    side: "left"
  }
];

const TOOLS = [
  {
    id: "pad-cells",
    label: "Text Tools — Pad Cells",
    title: "Pad Cells",
    badge: "DDMS Ready",
    description: "Pad selected cells to a fixed length.",
    render: renderPadCellsTool,
    afterRender: afterRenderPadCellsTool,
    run: runPadCellsTool
  }
];

function initializeApp(info) {
  appState.officeHost = info && info.host ? info.host : null;

  renderToolSelector();
  renderActiveTool();
}

function renderToolSelector() {
  const selector = document.getElementById("toolSelector");

  selector.innerHTML = TOOLS
    .map((tool) => {
      return `
        <option value="${escapeAttribute(tool.id)}" ${tool.id === appState.activeToolId ? "selected" : ""}>
          ${escapeHtml(tool.label)}
        </option>
      `;
    })
    .join("");

  selector.addEventListener("change", () => {
    appState.activeToolId = selector.value;
    renderActiveTool();
  });
}

function renderActiveTool() {
  const host = document.getElementById("toolHost");
  const tool = getActiveTool();

  if (!tool) {
    host.innerHTML = `
      <section class="tool-card">
        <div class="empty-state">This tool could not be loaded.</div>
      </section>
    `;
    return;
  }

  host.innerHTML = tool.render(tool);

  if (typeof tool.afterRender === "function") {
    tool.afterRender();
  }

  const runButton = document.querySelector("[data-action='run-tool']");

  if (runButton) {
    runButton.addEventListener("click", runActiveTool);
  }
}

function getActiveTool() {
  return TOOLS.find((tool) => tool.id === appState.activeToolId);
}

async function runActiveTool() {
  const tool = getActiveTool();

  if (!tool || appState.isBusy) {
    return;
  }

  if (appState.officeHost !== Office.HostType.Excel) {
    setStatus("This tool only works inside Excel.", "error");
    return;
  }

  setBusy(true);
  setStatus("Working on the selected cells...", "info");

  try {
    await tool.run();
  } catch (error) {
    console.error(error);
    setStatus(getFriendlyErrorMessage(error), "error");
  } finally {
    setBusy(false);
  }
}

function setBusy(isBusy) {
  appState.isBusy = isBusy;

  const button = document.querySelector("[data-action='run-tool']");

  if (!button) {
    return;
  }

  button.disabled = isBusy;
  button.textContent = isBusy ? "Applying..." : button.getAttribute("data-default-text");
}

function setStatus(message, type) {
  const status = document.getElementById("status");

  if (!status) {
    return;
  }

  status.className = `status ${type || "info"}`;
  status.textContent = message;
}

function clearStatus() {
  const status = document.getElementById("status");

  if (!status) {
    return;
  }

  status.className = "status";
  status.textContent = "";
}

/* -----------------------------
   Pad Cells Tool
------------------------------ */

function getDefaultPadSettings() {
  return {
    presetId: "custom",
    length: 10,
    padChar: "0",
    side: "left",
    formulaMode: "skip",
    skipBlanks: true,
    trimSpaces: true,
    forceTextFormat: true
  };
}

function loadPadSettings() {
  const defaults = getDefaultPadSettings();

  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);

    if (!saved) {
      return defaults;
    }

    const parsed = Object.assign({}, defaults, JSON.parse(saved));

    if (!parsed.presetId) {
      parsed.presetId = "custom";
    }

    return parsed;
  } catch (error) {
    return defaults;
  }
}

function savePadSettings(settings) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch (error) {
    // Ignore localStorage errors.
  }
}

function renderPadCellsTool(tool) {
  const settings = loadPadSettings();

  return `
    <section class="tool-card">
      <div class="tool-card-body">
        <div class="tool-title-row">
          <div>
            <h2>${escapeHtml(tool.title)}</h2>
            <p>${escapeHtml(tool.description)}</p>
          </div>

          <span class="tool-badge">${escapeHtml(tool.badge)}</span>
        </div>

        <div class="section-title">Format</div>

        <div class="format-row">
          <select id="padPreset" class="format-select" aria-label="Format">
            <option value="custom">Custom</option>
            ${renderPresetOptions()}
          </select>

          <button type="button" id="clearPresetButton" class="secondary-button">
            Reset
          </button>
        </div>

        <div id="currentChoice" class="current-choice"></div>

        <div class="section-title">Settings</div>

        <div class="form-grid">
          <div class="field">
            <label for="padLength">Length</label>
            <input id="padLength" type="number" min="1" step="1" value="${escapeAttribute(settings.length)}" />
          </div>

          <div class="field">
            <label for="padChar">Pad with</label>
            <input id="padChar" type="text" maxlength="1" value="${escapeAttribute(settings.padChar)}" />
          </div>
        </div>

        <div class="preview-panel">
          <div class="preview-label">Preview</div>
          <div id="previewLine" class="preview-line"></div>
        </div>

        <details class="advanced">
          <summary>Advanced options</summary>

          <div class="advanced-body">
            <div class="field full">
              <label for="padSide">Padding side</label>
              <select id="padSide">
                <option value="left" ${selected(settings.side, "left")}>Left side</option>
                <option value="right" ${selected(settings.side, "right")}>Right side</option>
              </select>
            </div>

            <div class="field full">
              <label for="formulaMode">Formula cells</label>
              <select id="formulaMode">
                <option value="skip" ${selected(settings.formulaMode, "skip")}>Skip formula cells</option>
                <option value="replace" ${selected(settings.formulaMode, "replace")}>Replace formulas with padded values</option>
              </select>
            </div>

            <label class="checkbox-row">
              <input id="skipBlanks" type="checkbox" ${checked(settings.skipBlanks)} />
              <span>Skip blank cells</span>
            </label>

            <label class="checkbox-row">
              <input id="trimSpaces" type="checkbox" ${checked(settings.trimSpaces)} />
              <span>Trim spaces before padding</span>
            </label>

            <label class="checkbox-row">
              <input id="forceTextFormat" type="checkbox" ${checked(settings.forceTextFormat)} />
              <span>Format as text</span>
            </label>
          </div>
        </details>
      </div>

      <div class="action-bar">
        <button
          type="button"
          class="primary-button"
          data-action="run-tool"
          data-default-text="Apply to Selected Cells"
        >
          Apply to Selected Cells
        </button>

        <div id="status" class="status" aria-live="polite"></div>
      </div>
    </section>
  `;
}

function renderPresetOptions() {
  return PAD_PRESETS
    .map((preset) => {
      return `
        <option value="${escapeAttribute(preset.id)}">
          ${escapeHtml(preset.label)} — ${escapeHtml(preset.description)}
        </option>
      `;
    })
    .join("");
}

function afterRenderPadCellsTool() {
  const settings = loadPadSettings();

  applyPadSettingsToForm(settings);
  updatePadUi(settings);

  document.getElementById("padPreset").addEventListener("change", handlePresetChanged);
  document.getElementById("clearPresetButton").addEventListener("click", resetToDefaultCustom);

  [
    "padLength",
    "padChar",
    "padSide",
    "formulaMode",
    "skipBlanks",
    "trimSpaces",
    "forceTextFormat"
  ].forEach((id) => {
    const element = document.getElementById(id);

    if (!element) {
      return;
    }

    element.addEventListener("input", handleManualPadSettingsChanged);
    element.addEventListener("change", handleManualPadSettingsChanged);
  });
}

function handlePresetChanged() {
  const presetId = document.getElementById("padPreset").value;

  if (presetId === "custom") {
    const settings = collectPadSettings(false);
    settings.presetId = "custom";

    updatePadUi(settings);
    savePadSettings(settings);
    clearStatus();
    return;
  }

  const preset = getPresetById(presetId);

  if (!preset) {
    return;
  }

  const current = collectPadSettings(false);

  const settings = Object.assign({}, current, {
    presetId: preset.id,
    length: preset.length,
    padChar: preset.padChar,
    side: preset.side,
    formulaMode: "skip",
    skipBlanks: true,
    forceTextFormat: true
  });

  applyPadSettingsToForm(settings);
  updatePadUi(settings);
  savePadSettings(settings);
  clearStatus();
}

function resetToDefaultCustom() {
  const settings = getDefaultPadSettings();

  applyPadSettingsToForm(settings);
  updatePadUi(settings);
  savePadSettings(settings);
  clearStatus();
}

function handleManualPadSettingsChanged() {
  const settings = collectPadSettings(false);

  settings.presetId = "custom";

  updatePadUi(settings);
  savePadSettings(settings);
  clearStatus();
}

function applyPadSettingsToForm(settings) {
  document.getElementById("padPreset").value = settings.presetId || "custom";
  document.getElementById("padLength").value = settings.length;
  document.getElementById("padChar").value = settings.padChar;
  document.getElementById("padSide").value = settings.side;
  document.getElementById("formulaMode").value = settings.formulaMode;
  document.getElementById("skipBlanks").checked = settings.skipBlanks;
  document.getElementById("trimSpaces").checked = settings.trimSpaces;
  document.getElementById("forceTextFormat").checked = settings.forceTextFormat;
}

function collectPadSettings(validate) {
  const lengthInput = document.getElementById("padLength").value;
  const padCharInput = document.getElementById("padChar").value;
  const side = document.getElementById("padSide").value;
  const formulaMode = document.getElementById("formulaMode").value;
  const presetId = document.getElementById("padPreset").value || "custom";

  const parsedLength = parseInt(lengthInput, 10);

  const settings = {
    presetId,
    length: Number.isInteger(parsedLength) ? parsedLength : 10,
    padChar: padCharInput ? padCharInput.substring(0, 1) : "0",
    side,
    formulaMode,
    skipBlanks: document.getElementById("skipBlanks").checked,
    trimSpaces: document.getElementById("trimSpaces").checked,
    forceTextFormat: document.getElementById("forceTextFormat").checked,
    isValid: true,
    errorMessage: ""
  };

  if (validate && (!Number.isInteger(parsedLength) || parsedLength < 1)) {
    settings.isValid = false;
    settings.errorMessage = "Enter a length of 1 or greater.";
  }

  if (validate && side !== "left" && side !== "right") {
    settings.isValid = false;
    settings.errorMessage = "Choose left or right padding.";
  }

  if (validate && formulaMode !== "skip" && formulaMode !== "replace") {
    settings.isValid = false;
    settings.errorMessage = "Choose how formula cells should be handled.";
  }

  return settings;
}

function updatePadUi(settings) {
  const presetSelector = document.getElementById("padPreset");

  if (presetSelector) {
    presetSelector.value = settings.presetId || "custom";
  }

  updateCurrentChoice(settings);
  updatePadPreview(settings);
}

function updateCurrentChoice(settings) {
  const currentChoice = document.getElementById("currentChoice");

  if (!currentChoice) {
    return;
  }

  const preset = getPresetById(settings.presetId);

  if (preset) {
    currentChoice.innerHTML = `
      <strong>${escapeHtml(preset.label)}</strong><br>
      ${escapeHtml(preset.description)}
    `;
    return;
  }

  currentChoice.innerHTML = `
    <strong>Custom</strong><br>
    Example default: left pad with zeros to length ${escapeHtml(settings.length)}.
  `;
}

function updatePadPreview(settings) {
  const previewLine = document.getElementById("previewLine");

  if (!previewLine) {
    return;
  }

  const before = settings.trimSpaces ? " 123 " : "123";
  const working = settings.trimSpaces ? before.trim() : before;
  const after = padText(working, settings.padChar, settings.length, settings.side);

  previewLine.innerHTML = `
    <span class="code-pill">${escapeHtml(visibleSpaces(before))}</span>
    &nbsp;becomes&nbsp;
    <span class="code-pill">${escapeHtml(visibleSpaces(after))}</span>
  `;
}

async function runPadCellsTool() {
  clearStatus();

  const settings = collectPadSettings(true);

  if (!settings.isValid) {
    setStatus(settings.errorMessage, "warning");
    return;
  }

  savePadSettings(settings);

  await Excel.run(async (context) => {
    const range = context.workbook.getSelectedRange();

    range.load(["values", "formulas", "address"]);
    await context.sync();

    const formulaCount = countFormulas(range.formulas);

    if (formulaCount > 0 && settings.formulaMode === "replace") {
      const shouldContinue = window.confirm(
        `Your selection contains ${formulaCount} formula cell(s).\n\n` +
        "This will replace formulas with padded text values.\n\n" +
        "Continue?"
      );

      if (!shouldContinue) {
        setStatus("Canceled. No cells were changed.", "info");
        return;
      }
    }

    const result = buildPaddedCellUpdates(range.values, range.formulas, settings);

    if (result.changedCellCount === 0) {
      const parts = ["No cells needed padding."];

      if (result.skippedFormulaCount > 0) {
        parts.push(`Skipped ${result.skippedFormulaCount} formula cell(s).`);
      }

      if (result.skippedBlankCount > 0) {
        parts.push(`Skipped ${result.skippedBlankCount} blank cell(s).`);
      }

      setStatus(parts.join(" "), "info");
      return;
    }

    result.updates.forEach((update) => {
      const cell = range.getCell(update.rowIndex, update.columnIndex);

      if (settings.forceTextFormat) {
        cell.numberFormat = [["@"]];
      }

      cell.values = [[update.value]];
    });

    await context.sync();

    const parts = [`Done. Updated ${result.changedCellCount} cell(s) in ${range.address}.`];

    if (result.skippedFormulaCount > 0) {
      parts.push(`Skipped ${result.skippedFormulaCount} formula cell(s).`);
    }

    if (result.skippedBlankCount > 0) {
      parts.push(`Skipped ${result.skippedBlankCount} blank cell(s).`);
    }

    setStatus(parts.join(" "), "success");
  });
}

function buildPaddedCellUpdates(values, formulas, settings) {
  const updates = [];
  let changedCellCount = 0;
  let skippedFormulaCount = 0;
  let skippedBlankCount = 0;

  values.forEach((row, rowIndex) => {
    row.forEach((value, columnIndex) => {
      const formulaCell = isFormulaCell(formulas, rowIndex, columnIndex);

      if (formulaCell && settings.formulaMode === "skip") {
        skippedFormulaCount += 1;
        return;
      }

      const blankCell = value === null || value === undefined || value === "";

      if (blankCell && settings.skipBlanks) {
        skippedBlankCount += 1;
        return;
      }

      const originalText = blankCell ? "" : String(value);
      const workingText = settings.trimSpaces ? originalText.trim() : originalText;

      if (settings.skipBlanks && workingText.length === 0) {
        skippedBlankCount += 1;
        return;
      }

      const paddedText = padText(workingText, settings.padChar, settings.length, settings.side);

      if (paddedText === originalText) {
        return;
      }

      updates.push({
        rowIndex,
        columnIndex,
        value: paddedText
      });

      changedCellCount += 1;
    });
  });

  return {
    updates,
    changedCellCount,
    skippedFormulaCount,
    skippedBlankCount
  };
}

function padText(text, padChar, targetLength, side) {
  const padCount = Math.max(0, targetLength - text.length);

  if (padCount === 0) {
    return text;
  }

  const padding = padChar.repeat(padCount);

  return side === "left" ? padding + text : text + padding;
}

/* Shared helpers */

function getPresetById(id) {
  return PAD_PRESETS.find((preset) => preset.id === id) || null;
}

function countFormulas(formulas) {
  let count = 0;

  formulas.forEach((row) => {
    row.forEach((formula) => {
      if (typeof formula === "string" && formula.charAt(0) === "=") {
        count += 1;
      }
    });
  });

  return count;
}

function isFormulaCell(formulas, rowIndex, columnIndex) {
  if (!formulas || !formulas[rowIndex]) {
    return false;
  }

  const formula = formulas[rowIndex][columnIndex];

  return typeof formula === "string" && formula.charAt(0) === "=";
}

function selected(actual, expected) {
  return actual === expected ? "selected" : "";
}

function checked(value) {
  return value ? "checked" : "";
}

function visibleSpaces(value) {
  return String(value).replace(/ /g, "·");
}

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function escapeAttribute(value) {
  return escapeHtml(value);
}

function getFriendlyErrorMessage(error) {
  if (!error) {
    return "Something went wrong.";
  }

  if (error.code === "ItemNotFound") {
    return "Select cells in Excel and try again.";
  }

  if (error.code === "InvalidSelection") {
    return "Select a valid cell range and try again.";
  }

  if (error.message) {
    return `Error: ${error.message}`;
  }

  return "Something went wrong while running the tool.";
}